import { useEffect, useState } from 'react'

/**
 * Whether the viewport is at/below `maxWidthPx`, read synchronously so there is
 * no first-paint flash. Feature-detects `matchMedia` (absent in some test DOMs
 * → `false`) and falls back to `addListener` for pre-EventTarget
 * MediaQueryList implementations.
 */
export const useIsNarrow = (maxWidthPx: number): boolean => {
	const query = `(max-width: ${maxWidthPx}px)`

	const [isNarrow, setIsNarrow] = useState<boolean>(() =>
		typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false,
	)

	useEffect(() => {
		if (typeof window.matchMedia !== 'function') return
		const mql = window.matchMedia(query)
		const onChange = (event: MediaQueryListEvent) => setIsNarrow(event.matches)
		if (typeof mql.addEventListener === 'function') {
			mql.addEventListener('change', onChange)
			return () => mql.removeEventListener('change', onChange)
		}
		// Legacy MediaQueryList: no EventTarget API.
		mql.addListener(onChange)
		return () => mql.removeListener(onChange)
	}, [query])

	return isNarrow
}
